import React, { useEffect, useState, useCallback } from 'react';
import { t } from '../../i18n';
import { Category, loadCategories, loadCategoriesFromExtension } from '../CategoryManager';
import './style.scss';

interface DailyDomainEntry {
  time: number;
}

interface PageData {
  totalTime: number;
  visitCount: number;
  lastVisited: number;
  title: string;
  daily?: Record<string, { time: number; visitCount: number }>;
}

interface DomainData {
  totalTime: number;
  lastVisited: number;
  pages: Record<string, PageData>;
  daily?: Record<string, DailyDomainEntry>;
}

interface ViewingTimeData {
  [domain: string]: DomainData;
}

type PeriodFilter = 'all' | 'today' | 'week' | 'month';

const CategoryStats: React.FC = () => {
  const [viewingData, setViewingData] = useState<ViewingTimeData>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriodState] = useState<PeriodFilter>(() => (localStorage.getItem('bls_catPeriod') as PeriodFilter) || 'all');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const setPeriod = (v: PeriodFilter) => { setPeriodState(v); localStorage.setItem('bls_catPeriod', v); };

  const getDateKeysForPeriod = (daily: Record<string, any> | undefined): string[] => {
    if (!daily) return [];
    const keys = Object.keys(daily);
    if (period === 'all') return keys;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return keys.filter(key => {
      const parts = key.split('-');
      if (parts.length === 2) {
        const monthStart = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
        const monthEnd = new Date(Number(parts[0]), Number(parts[1]), 0);
        if (period === 'today') return monthStart <= today && today <= monthEnd;
        if (period === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 6);
          return monthEnd >= weekAgo && monthStart <= today;
        }
        if (period === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 29);
          return monthEnd >= monthAgo && monthStart <= today;
        }
      } else {
        const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (period === 'today') return date.getTime() === today.getTime();
        if (period === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 6);
          return date >= weekAgo && date <= today;
        }
        if (period === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 29);
          return date >= monthAgo && date <= today;
        }
      }
      return true;
    });
  };

  const getDomainTimeForPeriod = (domainData: DomainData): number => {
    if (period === 'all') return domainData.totalTime;
    const keys = getDateKeysForPeriod(domainData.daily);
    return keys.reduce((sum, key) => sum + (domainData.daily?.[key]?.time || 0), 0);
  };

  const formatTime = (milliseconds: number): string => {
    if (milliseconds < 1000) return `0${t('timeSecond')}`;
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}${t('timeDay')} ${hours % 24}${t('timeHour')} ${minutes % 60}${t('timeMinute')}`;
    if (hours > 0) return `${hours}${t('timeHour')} ${minutes % 60}${t('timeMinute')}`;
    if (minutes > 0) return `${minutes}${t('timeMinute')} ${seconds % 60}${t('timeSecond')}`;
    return `${seconds}${t('timeSecond')}`;
  };

  const getCategoryTime = (cat: Category): number => {
    return cat.domains.reduce((sum, domain) => {
      const data = viewingData[domain];
      return sum + (data ? getDomainTimeForPeriod(data) : 0);
    }, 0);
  };

  const getCategoryDomainStats = (cat: Category) => {
    return cat.domains
      .map(domain => ({
        domain,
        time: viewingData[domain] ? getDomainTimeForPeriod(viewingData[domain]) : 0,
        hasData: !!viewingData[domain],
      }))
      .sort((a, b) => b.time - a.time);
  };

  const allAssignedDomains = new Set(categories.flatMap(c => c.domains));
  const uncategorizedDomains = Object.keys(viewingData).filter(d => !allAssignedDomains.has(d));
  const uncategorizedTime = uncategorizedDomains.reduce((sum, d) => sum + getDomainTimeForPeriod(viewingData[d]), 0);
  const uncategorizedDomainStats = uncategorizedDomains
    .map(domain => ({ domain, time: getDomainTimeForPeriod(viewingData[domain]), hasData: true }))
    .filter(d => period === 'all' || d.time > 0)
    .sort((a, b) => b.time - a.time);

  const totalTrackedTime = categories.reduce((sum, cat) => sum + getCategoryTime(cat), 0) + uncategorizedTime;

  const loadDataFromContentScript = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'VIEWING_TIME_DATA_RESPONSE') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success && event.data.data) {
            setViewingData(event.data.data);
            resolve(true);
          } else {
            resolve(false);
          }
        }
      };
      window.addEventListener('message', handleMessage);
      window.postMessage({ type: 'VIEWING_TIME_GET_DATA' }, '*');
      setTimeout(() => { window.removeEventListener('message', handleMessage); resolve(false); }, 3000);
    });
  }, []);

  const loadDataFromChromeStorage = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      try {
        if (window.chrome?.storage?.local) {
          window.chrome.storage.local.get(['viewing_time_data'], (result: any) => {
            if (chrome.runtime.lastError) { resolve(false); return; }
            try {
              setViewingData(result.viewing_time_data || {});
              resolve(true);
            } catch { resolve(false); }
          });
        } else {
          resolve(false);
        }
      } catch { resolve(false); }
    });
  }, []);

  const loadCategoriesData = useCallback(() => {
    const localCats = loadCategories();
    if (localCats.length > 0) {
      setCategories(localCats);
    }
    loadCategoriesFromExtension().then(cats => {
      if (cats.length > 0 || localCats.length === 0) {
        setCategories(cats);
      }
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      loadCategoriesData();
      const contentSuccess = await loadDataFromContentScript();
      if (contentSuccess) { setLoading(false); return; }
      const storageSuccess = await loadDataFromChromeStorage();
      if (storageSuccess) { setLoading(false); return; }
      setError(t('vtErrorConnection'));
      setLoading(false);
    };

    const handleExtensionReady = (event: MessageEvent) => {
      if (event.data.type === 'BLACKLIST_EXTENSION_READY') loadData();
    };
    window.addEventListener('message', handleExtensionReady);
    setTimeout(loadData, 500);
    return () => window.removeEventListener('message', handleExtensionReady);
  }, [loadDataFromContentScript, loadDataFromChromeStorage, loadCategoriesData]);

  const toggleCategory = (index: number) => {
    const next = new Set(expandedCategories);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setExpandedCategories(next);
  };

  if (loading) {
    return (
      <div className="category-stats-container">
        <div className="loading-state">{t('vtLoading')}</div>
      </div>
    );
  }

  const sortedCategories = [...categories].sort((a, b) => getCategoryTime(b) - getCategoryTime(a));

  return (
    <div className="category-stats-container">
      <h2 className="stats-title">{t('csTitle')}</h2>

      {error && <div className="error-message">{error}</div>}

      {categories.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p className="empty-title">{t('csEmptyTitle')}</p>
          <p className="empty-description">{t('csEmptyDesc')}</p>
        </div>
      ) : (
        <>
          <div className="controls-bar">
            <div className="sort-controls">
              <label className="sort-label">{t('vtPeriodLabel')}</label>
              <select
                value={period}
                onChange={e => setPeriod(e.target.value as PeriodFilter)}
                className="sort-select"
              >
                <option value="all">{t('vtPeriodAll')}</option>
                <option value="today">{t('vtPeriodToday')}</option>
                <option value="week">{t('vtPeriodWeek')}</option>
                <option value="month">{t('vtPeriodMonth')}</option>
              </select>
            </div>
            <div className="total-time">
              {t('csTotalTime')}: {formatTime(totalTrackedTime)}
            </div>
          </div>

          {period !== 'all' && (
            <p className="period-note">{t('vtPeriodNote')}</p>
          )}

          {/* バーチャート */}
          <div className="bar-chart">
            {sortedCategories.map((cat, i) => {
              const time = getCategoryTime(cat);
              const maxTime = Math.max(...sortedCategories.map(c => getCategoryTime(c)), uncategorizedTime, 1);
              const pct = (time / maxTime) * 100;
              return (
                <div key={i} className="bar-row">
                  <div className="bar-label">
                    <span className="color-dot" style={{ backgroundColor: cat.color }} />
                    {cat.name}
                  </div>
                  <div className="bar-track">
                    <div className="bar-fill" style={{ width: `${pct}%`, backgroundColor: cat.color }} />
                  </div>
                  <div className="bar-value">{formatTime(time)}</div>
                </div>
              );
            })}
            {uncategorizedDomainStats.length > 0 && (
              <div className="bar-row">
                <div className="bar-label">
                  <span className="color-dot uncategorized" />
                  {t('csUncategorized')}
                </div>
                <div className="bar-track">
                  <div className="bar-fill uncategorized" style={{ width: `${(uncategorizedTime / Math.max(...sortedCategories.map(c => getCategoryTime(c)), uncategorizedTime, 1)) * 100}%` }} />
                </div>
                <div className="bar-value">{formatTime(uncategorizedTime)}</div>
              </div>
            )}
          </div>

          {/* カテゴリ別詳細 */}
          <div className="categories-detail-list">
            {sortedCategories.map((cat) => {
              const catIndex = categories.indexOf(cat);
              const time = getCategoryTime(cat);
              const domainStats = getCategoryDomainStats(cat);
              const isExpanded = expandedCategories.has(catIndex);

              return (
                <div key={catIndex} className="category-detail-card">
                  <div
                    className={`category-detail-header ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleCategory(catIndex)}
                  >
                    <div className="category-info">
                      <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                      <span className="color-dot" style={{ backgroundColor: cat.color }} />
                      <span className="category-name">{cat.name}</span>
                      <span className="domain-count">{t('catDomainCount', { count: cat.domains.length })}</span>
                    </div>
                    <div className="category-time">{formatTime(time)}</div>
                  </div>

                  {isExpanded && (
                    <div className="domain-detail-list">
                      {domainStats.length === 0 ? (
                        <div className="no-domains">{t('csNoDomains')}</div>
                      ) : (
                        domainStats.map(({ domain, time, hasData }) => (
                          <div key={domain} className="domain-detail-item">
                            <span className="domain-name">{domain}</span>
                            <span className={`domain-time ${!hasData ? 'no-data' : ''}`}>
                              {hasData ? formatTime(time) : t('csNoData')}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* 未分類 */}
            {uncategorizedDomainStats.length > 0 && (() => {
              const uncatKey = -1;
              const isExpanded = expandedCategories.has(uncatKey);
              return (
                <div className="category-detail-card uncategorized">
                  <div
                    className={`category-detail-header ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleCategory(uncatKey)}
                  >
                    <div className="category-info">
                      <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                      <span className="color-dot uncategorized" />
                      <span className="category-name">{t('csUncategorized')}</span>
                      <span className="domain-count">{t('catDomainCount', { count: uncategorizedDomainStats.length })}</span>
                    </div>
                    <div className="category-time">{formatTime(uncategorizedTime)}</div>
                  </div>

                  {isExpanded && (
                    <div className="domain-detail-list">
                      {uncategorizedDomainStats.map(({ domain, time }) => (
                        <div key={domain} className="domain-detail-item">
                          <span className="domain-name">{domain}</span>
                          <span className="domain-time">{formatTime(time)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryStats;
